<template>
    <div>
        <template v-if="routinesLoading">
            <h1><div class="count-blinker"></div> rutiner</h1>
        </template>
        <template v-else-if="routines && routines.length > 0">
            <h1>{{ routines[0].rowCount }} rutine<template v-if="routines.length > 1">r</template></h1>
        </template>
        <template v-else>
            <h1>Ingen rutiner</h1>
        </template>

        <div class="list-filter" v-if="activeFilters">
            <div class="list-filter-group">
                <template v-if="supplyTypes && supplyTypes.length > 1">
                    <div class="list-filter-item" v-for="supplyType in supplyTypes" :key="supplyType.sysBusinessAreaTypeId" v-if="getFilters.supplyType.includes(supplyType.sysBusinessAreaTypeId)" :class="'list-filter-item--' + supplyType.systemName">
                        <span class="list-filter-item-label">Forretningsområde:</span><span class="list-filter-item-value"><icon class="tiny-svg" :name="supplyType.systemName" />{{ supplyType.description }}</span>
                        <button class="list-filter-item-button" @click="removeSupplyTypeFilter(supplyType.sysBusinessAreaTypeId)">
                            <span class="visually-hidden">Fjern filter</span>
                            <span aria-hidden="true">&#10761;</span>
                        </button>
                    </div>
                </template>

                <div class="list-filter-item" v-if="getFilters.address">
                    <span class="list-filter-item-label">Adresse:</span><span class="list-filter-item-value">{{ getFilters.address }}</span>
                    <button class="list-filter-item-button" @click="removeAddressFilter()">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-if="getFilters.routines.showClosed">
                    <span class="list-filter-item-label">Status:</span><span class="list-filter-item-value">Vis også afsluttede</span>
                    <button class="list-filter-item-button" @click="removeRoutineFilter('showClosed')">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-for="responsible in getFilters.routines.responsible" :key="responsible">
                    <span class="list-filter-item-label">Ansvarlig:</span><span class="list-filter-item-value">{{ caseworkers.find(item => item.userId === responsible).name }}</span>
                    <button class="list-filter-item-button" @click="removeRoutineFilter('responsible', responsible)">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-for="company in getFilters.routines.company" :key="company">
                    <span class="list-filter-item-label">Virksomhed:</span><span class="list-filter-item-value">{{ firms.find(item => item.id === company).name }}</span>
                    <button class="list-filter-item-button" @click="removeRoutineFilter('company', company)">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>

                <div class="list-filter-item" v-if="getFilters.routines.name">
                    <span class="list-filter-item-label">Rutinenavn:</span><span class="list-filter-item-value">{{ getFilters.routines.name }}</span>
                    <button class="list-filter-item-button" @click="removeRoutineFilter('name')">
                        <span class="visually-hidden">Fjern filter</span>
                        <span aria-hidden="true">&#10761;</span>
                    </button>
                </div>
            </div>
        </div>

        <pagination v-show="routines && routines.length > 0 && routines[0].pageCount > 1 && !routinesLoading" :showPaging="routines && routines.length > 0 && routines[0].pageCount > 1 ? true : false" :totalPages="routines && routines.length > 0 ? routines[0].pageCount : null" :page="'routines'" :position="'right'" />
        <div v-if="routines && routines.length > 0 && !routinesLoading" class="grid-x list-sort">
            <div class="cell large-1"></div>
            <div class="cell large-2">
                <button :aria-selected="currentSort === 'taskName'" @click="sort('taskName')">Navn<icon v-if="currentSort === 'taskName'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'taskName'}" name="keyboard_arrow_sort" /></button>
            </div>
            <div class="cell large-2">
                <button :aria-selected="currentSort === 'routineCount'" @click="sort('routineCount')">Antal rutiner<icon v-if="currentSort === 'routineCount'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'routineCount'}" name="keyboard_arrow_sort" /></button>
            </div>
            <div class="cell large-2">
                Entreprenør
            </div>
            <div class="cell large-2">
                <button :aria-selected="currentSort === 'nextDate'" @click="sort('nextDate')">Udføres næste gang<icon v-if="currentSort === 'nextDate'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'nextDate'}" name="keyboard_arrow_sort" /></button>
            </div>
            <div class="cell large-2">
                <button :aria-selected="currentSort === 'ownerUser'" @click="sort('ownerUser')">Ansvarlig<icon v-if="currentSort === 'ownerUser'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'ownerUser'}" name="keyboard_arrow_sort" /></button>
            </div>
            <div class="cell large-1">
                <button :aria-selected="currentSort === 'status'" @click="sort('status')">Status<icon v-if="currentSort === 'status'" class="small-svg fill-selected-dark-gray" :class="{rotated: !currentSortDir && currentSort === 'status'}" name="keyboard_arrow_sort" /></button>
            </div>
        </div>
        <transition-group name="list" tag="div" v-if="!routinesLoading">
            <div class="grid-x list-row supply align-items" :class="businessAreaClass(routine.businessAreaTypeId)" v-for="routine in routines" :key="routine.taskId">
                <div class="cell large-1">
                    <icon class="task-svg medium-svg fill-primary" name="routine" />
                </div>
                <div class="cell large-2 item truncate-fade">
                    <span>{{ routine.taskName }}</span>
                    <span v-if="routine.address1">{{ routine.address1 }}, <span v-if="routine.address2">{{ routine.address2 }},</span> {{ routine.postalCode }} {{ routine.city }}</span>
                    <span v-else>Ingen adresse</span>
                </div>
                <div class="cell large-2 item truncate-fade">
                    <span v-if="routine.routineCount">{{ routine.routineCount }} rutine<span v-if="routine.routineCount > 1">r</span></span>
                    <span v-if="routine.intervalList">{{ routine.intervalList }}</span>
                    <span v-else>Ingen rutiner</span>
                </div>
                <div class="cell large-2 item truncate-fade" v-if="routine && (routine.contractorList && routine.contractorList.length > 0) && (routine.contractorCount && routine.contractorCount > 0)">
                    <span v-if="routine && routine.contractorList">{{routine.contractorList}}</span>
                    <span v-if="routine && routine.contractorCount">{{routine.contractorCount}} entreprenør<span v-if="routine.contractorCount !== 1">er</span></span>
                </div>
                <div v-else class="cell large-2 item truncate-fade flexbox-align-items">
                    <span>Ingen entreprenører</span>
                </div>
                <div class="cell large-2 item truncate-fade flexbox-align-items">
                    <!-- <span v-if="routine.nextLatestDate">Slutdato</span> -->
                    <span v-if="routine.nextDate">{{ routine.nextDate | formatDateShortElapsed }}</span>
                    <span v-else>Ingen dato</span>
                </div>
                <div class="cell large-2 item truncate-fade">
                    <span>{{ routine.ownerUser }}</span>
                    <span v-if="!routine.ownerActive"><em>Inaktiv bruger</em></span>
                    <span v-else>{{ routine.ownerOrganization }}</span>
                </div>
                <div class="cell large-1 item flexbox-center">
                    <div class="task-status" :class="statusColor(routine.status)">{{ routine.status }}</div>
                </div>
                <router-link class="dashboard-list-link" :to="{ name: 'taskid', params: { id: routine.taskId } }">
                    <span class="visually-hidden">Åben opgave</span>
                </router-link>
            </div>
        </transition-group>
        <pagination v-show="routines && routines.length > 0 && routines[0].pageCount > 1 && !routinesLoading" :showPaging="routines && routines.length > 0 && routines[0].pageCount > 1 ? true : false" :totalPages="routines && routines.length > 0 ? routines[0].pageCount : null" :page="'routines'" :position="'right'" />

        <div class="dashboard-list-empty" v-if="!routinesLoading && routines && routines.length === 0">
            <template v-if="activeFilters">
                <h2>Ingen rutiner, prøv at ændre en af følgende ting:</h2>
                <ul>
                    <li v-if="getFilters.supplyType.length < supplyTypes.length">Vælg et andet forretningsområde</li>
                    <li v-if="getFilters.routines.company.length > 1 || getFilters.routines.responsible.length > 1">Vælg en anden ansvarlig eller virksomhed</li>
                    <li v-if="getFilters.address">Fjern søgning efter adresse</li>
                    <li>Fjern et eller flere filtre</li>
                </ul>
            </template>
            <template v-else>
                <p><em>Ingen rutiner oprettet endnu...</em></p>
            </template>
        </div>

        <div class="text-center dashboard-list-loading" v-if="routinesLoading || !routines">
            Indlæser rutiner...
            <div class="list-loader">
                <div class="bar"></div>
                <div class="bar"></div>
                <div class="bar"></div>
                <div class="visually-hidden">Indlæser...</div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import pagination from './../../../components/shared/pagination/pagination.vue'

export default {
    name: 'list-routines',
    components: {
        pagination
    },
    props: {
        value: Array
    },
    data () {
        return {
            routines: this.value
        }
    },
    computed: {
        ...mapGetters({
            currentSortDir: 'getCurrentRoutineOrderByDescending',
            currentSort: 'getCurrentRoutineOrderByColumnName',

            routinesLoading: 'getRoutineLoading',

            // Filters
            supplyTypes: 'getBusinessAreaTypes',
            caseworkers: 'caseWorkerList',
            firms: 'getAllFirmsForActivity',
            getFilters: 'getFilters'
        }),

        supplyTypesFormatted () {
            let formatted = ''

            this.supplyTypes.forEach((supplyType, index) => {
                if (index > 0) {
                    formatted += ', '
                }
                formatted += supplyType.description
            })

            return formatted
        },
        activeFilters () {
            if (this.supplyTypes.length > 1 || this.getFilters.address || this.getFilters.routines.name || (this.getFilters.routines.responsible && this.getFilters.routines.responsible.length > 0) || this.getFilters.routines.showClosed || (this.getFilters.routines.company && this.getFilters.routines.company.length > 0)) {
                return true
            }

            return false
        }
    },
    watch: {
        value (routines) {
            this.routines = routines
        },

        getFilters: {
            deep: true,
            handler () {
                this.getFilteredList()
            }
        }
    },
    methods: {
        ...mapActions({
            getRoutines: 'getQueryRoutineList',
            setActivePages: 'setActivePages',
            removeFilters: 'removeFilters'
        }),

        sort (property) {
            let sort = this.currentSortDir
            const filterObject = {
                orderByDescending: this.currentSort !== property ? false : sort = !sort,
                orderByColumnName: property,
                tab: 'routines',
                page: 1
            }

            this.setActivePages(filterObject)
        },
        businessAreaClass (businessAreaType) {
            return this.supplyTypes.find(supplyType => supplyType.sysBusinessAreaTypeId === businessAreaType).systemName
        },
        statusColor (status) {
            if (status) {
                if (status.toLowerCase() === 'i gang') {
                    return 'blue-status'
                }
                if (status.toLowerCase() === 'afsluttet') {
                    return 'gray-status'
                }
            }
            return 'blue-status'
        },
        getFilteredList () {
            const filter = this.getFilters.routines
            const routineQuery = {}

            if (filter.name) {
                routineQuery.whereLikeTaskName = filter.name
            }
            if (filter.responsible) {
                routineQuery.whereOwnerUserIdList = filter.responsible
            }
            if (filter.showClosed) {
                routineQuery.whereIsClosed = filter.showClosed
            }
            if (filter.company) {
                routineQuery.whereSentToOrganizationIdList = filter.company.length > 0 ? filter.company : null
            }

            if (this.getFilters.supplyType) {
                routineQuery.whereBusinessAreaTypeIdList = this.getFilters.supplyType
            }

            if (this.getFilters.address) {
                routineQuery.whereLikeOccurrenceAddress = this.getFilters.address
            }

            this.getRoutines(routineQuery)
        },
        removeSupplyTypeFilter (value) {
            if (this.getFilters.supplyType.length === 1) {
                this.$notify({
                    group: 'info',
                    type: 'warn',
                    text: 'Der skal være valgt minimum et forretningsområde.',
                    duration: 7500
                })
                return
            }

            this.removeFilters({
                filter: 'supplyType',
                value: value
            })
        },
        removeAddressFilter () {
            this.removeFilters({
                filter: 'address',
                value: ''
            })
        },
        removeRoutineFilter (filter, value) {
            if (Array.isArray(this.getFilters.routines[filter]) && filter === 'responsible') {
                const element = this.$parent.$children.find(child => child.$options.name === 'dashboard-filters').$refs[`enquiry${filter[0].toUpperCase() + filter.slice(1)}`]

                if (element) {
                    element.checked = element.checked.filter(item => item.userId !== value)
                }
            }
            if (typeof this.getFilters.routines[filter] === 'string' && filter === 'name') {
                this.$parent.$children.find(child => child.$options.name === 'dashboard-filters').$refs.routineNameSearch.value = ''
            }
            this.removeFilters({
                page: 'routines',
                filter: filter,
                value: value
            })
        }
    },
    mounted () {
        this.getFilteredList()
    }
}
</script>



// WEBPACK FOOTER //
// src/components/shared/list/list-routines.vue